import { Elements } from '@stripe/react-stripe-js'
import type { FileDto } from '@viastud/server/services/file_service'
import {
  allowedWindows,
  MIN_TRIAL_COMMENT_LENGTH,
} from '@viastud/server/services/reservation/reservation_verification_utils'
import { Button } from '@viastud/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@viastud/ui/dialog'
import { FileInput } from '@viastud/ui/file-input'
import { useToast } from '@viastud/ui/hooks/use-toast'
import { useUploadFile } from '@viastud/ui/hooks/use-upload-file'
import { createDate } from '@viastud/ui/lib/date-service'
import { trpc } from '@viastud/ui/lib/trpc'
import { SegmentedControl } from '@viastud/ui/segmented-control'
import { Textarea } from '@viastud/ui/textarea'
import type { policyMessages } from '@viastud/utils'
import { buildPolicyMessage, type Grade } from '@viastud/utils'
import type { Dayjs } from 'dayjs'
import { LoaderCircle, Minus, Plus, X } from 'lucide-react'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'

import { CalendarPicker } from '@/components/course/calendar-picker.tsx'
import { MiniHeader } from '@/components/course/mini-header.tsx'
import PaymentForm from '@/components/payment-form.tsx'
import { getStripe } from '@/lib/stripe'
import { useStudentSubscriptionPresenter } from '@/presenters/student-subscription.presenter.ts'
import { gtmService } from '@/services/gtm-service.ts'
import { useAuthStore } from '@/store/auth.store.ts'

import type Sheet from '../../../../server/app/models/sheet.ts'

type DurationInMinute = 30 | 60 | 90

interface Slot {
  time: string
  date: Dayjs
  duration: DurationInMinute
}

interface CalendarSlotItem {
  time: string
  available: boolean
  slotIds: number[]
  professorsAvailabilityIds: number[]
}

interface BookingFromQuery {
  success: boolean
  date: string
  time: string
  duration?: number
}

interface BookingModalProps {
  sheet: Pick<Sheet, 'id' | 'name'>
  grade: Grade
  bookingFromQuery?: BookingFromQuery | null
  autoOpenBooking: boolean
}

export const BookingModal: React.FC<BookingModalProps> = ({
  sheet,
  grade,
  bookingFromQuery,
  autoOpenBooking,
}) => {
  const UNITARY_PRICE = grade === 'TERMINALE' || grade === 'PREMIERE' ? 29.99 : 19.99
  const DURATIONS: { label: string; value: DurationInMinute; disabled: boolean }[] = [
    { label: '1 heure', value: 60, disabled: grade === 'PREMIERE' || grade === 'TERMINALE' },
    {
      label: '1 heure 30 minutes',
      value: 90,
      disabled:
        grade === 'SIXIEME' ||
        grade === 'CINQUIEME' ||
        grade === 'QUATRIEME' ||
        grade === 'TROISIEME' ||
        grade === 'SECONDE',
    },
  ] as const

  const [isMakeReservationLoading, setIsMakeReservationLoading] = useState(false)
  const { data: tokenInfo, refetch: refetchBalance } = trpc.user.getTokenBalance.useQuery()
  const isTrialCourse = sheet.name.toLowerCase().includes("cours d'essai")
  const isBookingDisabled = tokenInfo?.hasSignUpBonus && !isTrialCourse

  const { mutateAsync: payForIndividualLessons } =
    trpc.payment.createSingleLessonPaymentIntent.useMutation()
  const { mutateAsync: makeReservationMutate } =
    trpc.studentAvailabilities.createReservation.useMutation()
  const {
    data: subscription,
    refetch: refetchStudentDetail,
    isLoading: subscriptionLoading,
  } = trpc.user.getStudentSubscriptionDetails.useQuery()

  const presenter = useStudentSubscriptionPresenter()
  const { toast } = useToast()
  const { uploadFiles } = useUploadFile()

  const [open, setOpen] = useState<boolean>(autoOpenBooking)
  const [step, setStep] = useState(1)
  const [weekStart, setWeekStart] = useState(() => createDate().startOf('week'))
  const [duration, setDuration] = useState<DurationInMinute>(
    DURATIONS.find((d) => !d.disabled)?.value ?? 60
  )
  const [selectedDate, setSelectedDate] = useState(createDate().format('YYYY-MM-DD'))
  const [selectedHour, setSelectedHour] = useState<string | null>(null)
  const [quantity, setQuantity] = useState(1)
  const [reservationImages, setReservationImages] = useState<FileDto[]>([])

  const getPolicyMessageKey = (): keyof typeof policyMessages => {
    if (!presenter.promotionalCodeOffer) {
      return withCommitment ? 'BASE_COMMITMENT' : 'BASE_NO_COMMITMENT'
    }

    if (presenter.promotionalCodeOffer === 'VIASTUD_30_1_MOIS' && withCommitment) {
      return 'VIASTUD_30_1_MOIS'
    } else if (presenter.promotionalCodeOffer === 'VIASTUD_20_1_MOIS' && !withCommitment) {
      return 'VIASTUD_20_1_MOIS'
    } else if (presenter.promotionalCodeOffer === 'VIASTUD_30' && withCommitment) {
      return 'VIASTUD_30'
    } else {
      return withCommitment ? 'BASE_COMMITMENT' : 'BASE_NO_COMMITMENT'
    }
  }

  const getPolicyMessage = () => {
    try {
      const price = (presenter.firstSubscriptionPrice / 100).toFixed(2).replace('.', ',')
      const monthly = String(presenter.studentPlan?.price ?? '').replace('.', ',')
      return buildPolicyMessage(getPolicyMessageKey(), {
        first_price: price,
        monthly_price: monthly,
      })
    } catch {
      return ''
    }
  }

  const trueDuration = tokenInfo?.hasSignUpBonus ? 30 : duration
  const tokenBalance = tokenInfo?.balance ?? 0
  const amount = 1
  const { user } = useAuthStore()
  const isSuspended = presenter?.subscriptionDetails?.status === 'SUSPENDED'

  //individual payment
  const [isIndividualPaymentOpen, setIsIndividualPaymentOpen] = useState(false)
  const [individualPaymentClientSecret, setIndividualPaymentClientSecret] = useState<string | null>(
    null
  )
  const [withCommitment, setWithCommitment] = useState<boolean>(false)

  useEffect(() => {
    if (bookingFromQuery?.success) {
      setOpen(true)
      setSelectedDate(bookingFromQuery.date)
      setSelectedHour(bookingFromQuery.time)
      setDuration(bookingFromQuery.duration === 90 ? 90 : 60)
      setWeekStart(createDate(bookingFromQuery.date).startOf('week'))
      setStep(3)
    }
  }, [bookingFromQuery])

  // Generate the 7 days for the current week view
  const weekDays = Array.from({ length: 7 }).map((_, index) => {
    const date = weekStart.add(index, 'day')

    return {
      fullDate: date.format('YYYY-MM-DD'),
      dayName: date.format('ddd'),
      dayNumber: date.date(),
      dayOfWeek: (date.day() + 6) % 7,
    }
  })

  const {
    data: availableSlots,
    refetch: refetchAvailableSlots,
    error,
    isLoading,
  } = trpc.studentAvailabilities.getStudentAvailabilities.useQuery(
    {
      sheetId: sheet.id,
      weekStart: weekStart.format('YYYY-MM-DD'),
      duration: trueDuration,
    },
    { enabled: open && step === 1 && !!tokenInfo && !isBookingDisabled, retry: false }
  )

  const selectedDay = selectedDate ? (createDate(selectedDate).day() + 6) % 7 : null
  const slotsForSelectedDay = useMemo<CalendarSlotItem[]>(() => {
    if (selectedDay === null) return []

    const windowForDay = allowedWindows[selectedDay]
    if (!windowForDay) return []

    const availableByTime = new Map<string, CalendarSlotItem>()
    if (availableSlots && availableSlots.length > 0) {
      availableSlots
        .filter((s) => s.dayOfWeek === selectedDay)
        .forEach((s) => {
          const time = `${s.hour.toString().padStart(2, '0')}:${s.minute === 0 ? '00' : '30'}`
          availableByTime.set(time, {
            time: time,
            available: true,
            slotIds: s.slotIds,
            professorsAvailabilityIds: s.professorAvailabilityIds,
          })
        })
    }

    const startMinutes = windowForDay.startHour * 60
    const endMinutes = windowForDay.endHourInclusive * 60
    const result: CalendarSlotItem[] = []
    for (let t = startMinutes; t + 60 <= endMinutes; t += 60) {
      const hour = Math.floor(t / 60)
      const time = `${hour.toString().padStart(2, '0')}:00`

      const slotData = availableByTime.get(time)
      const isAvailable = !!slotData

      result.push({
        time,
        available: isAvailable,
        slotIds: slotData ? slotData.slotIds : [],
        professorsAvailabilityIds: slotData ? slotData.professorsAvailabilityIds : [],
      })
    }

    return result
  }, [availableSlots, selectedDay])

  const handlePrevWeek = () => {
    setWeekStart((currentWeekStart) => {
      const previousWeekStart = currentWeekStart.subtract(1, 'week').startOf('week')
      const today = createDate().startOf('day')

      if (previousWeekStart.isBefore(today)) {
        setSelectedDate(today.format('YYYY-MM-DD'))
        return today.startOf('week')
      }

      setSelectedDate(previousWeekStart.format('YYYY-MM-DD'))
      return previousWeekStart
    })
    setSelectedHour(null)
  }

  const handleNextWeek = () => {
    setWeekStart((w) => {
      const nextWeekStart = w.add(1, 'week').startOf('week')
      setSelectedDate(nextWeekStart.format('YYYY-MM-DD'))
      return nextWeekStart
    })
    setSelectedHour(null)
  }

  const handleReserveLesson = async (comment: string, images: FileDto[]) => {
    try {
      setIsMakeReservationLoading(true)
      if (!selectedHour) {
        return toast({
          title: 'Erreur de réservation',
          description: 'Veuillez sélectionner une heure avant de réserver votre cours.',
          variant: 'destructive',
        })
      }
      if (!availableSlots) {
        return toast({
          title: 'Erreur de réservation',
          description: 'Veuillez sélectionner un slot pour réserver votre cours.',
          variant: 'destructive',
        })
      }
      const selectedSlot = availableSlots.find((slot) => {
        const day = (createDate(selectedDate).day() + 6) % 7
        const [hourStr, minuteStr] = selectedHour.split(':')
        return (
          slot.dayOfWeek === day &&
          slot.hour === parseInt(hourStr) &&
          slot.minute === parseInt(minuteStr)
        )
      })

      if (!selectedSlot) {
        return toast({
          title: 'Erreur de réservation',
          description: 'Ce créneau n’est pas disponible.',
          variant: 'destructive',
        })
      }

      let imageIds: string[] | undefined = undefined
      if (images.length > 0) {
        const uploadedFiles = await uploadFiles(images)
        imageIds = uploadedFiles.map((f) => f.id)
      }

      const [hourStr, minuteStr] = selectedHour.split(':')
      const dayOfWeek = (createDate(selectedDate).day() + 6) % 7
      const weekStartFormatted = weekStart.format('YYYY-MM-DD')

      await makeReservationMutate({
        sheetId: sheet.id,
        duration: trueDuration,
        studentComment: comment.trim() || undefined,
        imageIds,
        selectedSlot: {
          weekStart: weekStartFormatted,
          dayOfWeek: dayOfWeek,
          hour: parseInt(hourStr),
          minute: parseInt(minuteStr),
          slotIds: selectedSlot.slotIds,
          professorsAvailabilityIds: selectedSlot.professorAvailabilityIds,
        },
      })

      toast({
        title: 'Réservation réussie',
        description: 'Votre cours a été réservé avec succès.',
      })

      gtmService.trackCourseReservationSuccess()
      await refetchBalance()
      await refetchStudentDetail()
      handleOnClose()
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'NOT_ENOUGH_TOKENS') {
          toast({
            title: 'Erreur de réservation',
            description: "Vous n'avez pas assez de fond sur votre compte",
            variant: 'destructive',
          })
        } else {
          toast({
            title: 'Erreur de réservation',
            description:
              'Une erreur est survenue lors de la réservation de votre cours. Veuillez réessayer.',
            variant: 'destructive',
          })
        }
      }
    } finally {
      setIsMakeReservationLoading(false)
    }
  }

  const handleOnBack = () => {
    if (step === 2) {
      setStep(1)
    } else if (step === 3) {
      setStep(1)
    }
  }

  const handleOnClose = () => {
    // Reset state on close
    setStep(1)
    setWeekStart(createDate().startOf('week'))
    setSelectedDate(createDate().format('YYYY-MM-DD'))
    setSelectedHour(null)
    setQuantity(1)
    setReservationImages([])

    const url = new URL(window.location.href)
    url.searchParams.delete('book')

    //reset uri
    if (bookingFromQuery?.success) {
      url.searchParams.delete('success')
      url.searchParams.delete('date')
      url.searchParams.delete('time')
      url.searchParams.delete('duration')
    }
    window.history.replaceState({}, document.title, url.toString())
    setOpen(false)
  }

  const handleNextStep = () => {
    if (step === 1) {
      if (subscription?.status === 'SUSPENDED') {
        handleOnClose()
        return
      }

      if (subscription?.status === 'ACTIVE' || (tokenInfo?.balance ?? 0) >= amount) {
        setStep(3)
      } else {
        setStep(2)
      }
    }
  }

  const handlePayForIndividualLessons = async () => {
    try {
      const { clientSecret } = await payForIndividualLessons({
        quantity,
      })
      setIndividualPaymentClientSecret(clientSecret)
      setIsIndividualPaymentOpen(true)
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: 'Erreur de paiement',
          description:
            error.message ?? 'Une erreur est survenue lors du paiement. Veuillez réessayer.',
          variant: 'destructive',
        })
      }
    }
  }

  /**
   * Booking Step: Select Date and Time
   * @constructor
   */
  const BookingStepSelectDate: React.FC = () => {
    return (
      <>
        {/* Mini Header */}
        <MiniHeader
          onClose={() => {
            handleOnClose()
          }}
        />

        {/* Header */}
        <h2 className="mb-1 mt-2 text-3xl font-semibold">
          Réservez un cours {tokenInfo?.hasSignUpBonus ? 'gratuit' : ''}
        </h2>

        {/* Content */}
        <div className="flex-1 py-2">
          <div className="flex flex-col gap-y-2">
            {tokenInfo?.hasSignUpBonus ? (
              <div className="my-3 rounded-2xl border border-blue-300 bg-blue-50 px-4 py-3 text-blue-800">
                Vous bénéficiez d&#39;un cours gratuit grâce à votre bonus de bienvenue !
              </div>
            ) : (
              <>
                <div className="font-medium">Durée du cours</div>
                <SegmentedControl
                  items={DURATIONS}
                  value={duration}
                  onChange={setDuration}
                  className="self-start"
                />
              </>
            )}

            {error?.message.includes('pénalité de 48h') ? (
              <div className="flex flex-col gap-y-2 rounded-2xl border border-red-300 bg-red-50 px-4 py-3 text-justify text-red-800">
                <p>{error.message}</p>
                <p>
                  Vous pourrez effectuer une nouvelle réservation une fois la période de pénalité de
                  48h écoulée.
                </p>
              </div>
            ) : error?.message === 'Already has a reservation' ? (
              <p> Vous avez déjà une réservation en cours </p>
            ) : error ? (
              <div
                role="alert"
                className="rounded-2xl border border-red-300 bg-red-50 px-4 py-3 text-red-800"
              >
                <p>Impossible de charger les disponibilités : {error.message}</p>
                <Button variant="outline" onClick={() => void refetchAvailableSlots()}>
                  Réessayer
                </Button>
              </div>
            ) : (
              <CalendarPicker
                rangeLabel={`${weekStart.format('D')} – ${weekStart.add(6, 'day').format('D MMM YYYY')}`}
                days={weekDays}
                slots={slotsForSelectedDay}
                selectedDate={selectedDate}
                selectedTime={selectedHour}
                loading={isLoading}
                onPrev={handlePrevWeek}
                onNext={handleNextWeek}
                onSelectDate={(date) => {
                  setSelectedDate(date)
                }}
                onSelectTime={(time) => {
                  setSelectedHour(time)
                }}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="h-fit pt-4">
          <Button
            className="w-full"
            disabled={!selectedDate || !selectedHour || !!error || isLoading}
            onClick={() => {
              handleNextStep()
            }}
          >
            Continuer
          </Button>
        </div>
      </>
    )
  }

  /**
   * Booking Step: Pay one or more lessons or subscribe to a plan
   * @constructor
   */
  const BookingStepPayment: React.FC<{ slot: Slot; onBack: () => void }> = ({ onBack, slot }) => {
    const url = new URL(window.location.href)
    const cleanUrl = url.origin + url.pathname
    const returnUrl = `${cleanUrl}?success=true&date=${slot.date.format('YYYY-MM-DD')}&time=${slot.time}&duration=${slot.duration}`

    const getPrice = () => {
      const monthlyPrice = presenter.studentPlan?.price
      if (monthlyPrice === undefined) return '...'
      const priceWithCommitment = monthlyPrice
      const priceNoCommitment = monthlyPrice
      let basePrice = withCommitment ? priceWithCommitment : priceNoCommitment

      if (presenter.promotionalCodeOffer === 'VIASTUD_30_1_MOIS' && withCommitment) {
        basePrice = priceWithCommitment * (1 - 0.33)
      } else if (presenter.promotionalCodeOffer === 'VIASTUD_20_1_MOIS' && !withCommitment) {
        basePrice = priceNoCommitment - 30
      } else if (presenter.promotionalCodeOffer === 'VIASTUD_30' && withCommitment) {
        basePrice = priceWithCommitment * (1 - 0.33)
      }
      return basePrice.toFixed(2).replace('.', ',')
    }

    if (subscriptionLoading) {
      return (
        <>
          {/* Mini Header */}
          <MiniHeader
            onBack={onBack}
            onClose={() => {
              handleOnClose()
            }}
          />

          {/* Loading */}
          <div className="flex min-h-[10em] flex-1 items-center justify-center">
            <div className="loader">Loading...</div>
          </div>
        </>
      )
    }

    return (
      <>
        {/* Mini Header */}
        <MiniHeader
          onBack={onBack}
          onClose={() => {
            handleOnClose()
          }}
        />

        {/* Header */}
        <h2 className="mb-1 mt-2 text-3xl font-semibold">Réservez un cours</h2>
        <p className="text-sm text-gray-600">
          Profite de nos abonnements ou de nos cours à l&#39;unité.
        </p>

        {/* Content */}
        <div className="flex-1 overflow-y-auto py-3">
          <div className="flex flex-col gap-y-3">
            {/* Subscription plan payment (moved first) */}
            <div className="rounded-2xl border border-gray-300 p-5 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-lg font-semibold">Illimité</h4>
                <div className="flex flex-col justify-end">
                  <div className="text-4xl font-bold">{getPrice()}€</div>
                </div>
              </div>

              {/* Engagement selector */}
              <SegmentedControl<boolean>
                className="mb-4"
                items={[
                  // { label: 'Avec engagement', value: true },
                  { label: 'Sans engagement', value: false },
                ]}
                value={withCommitment}
                onChange={setWithCommitment}
              />

              <p className="mb-4 text-sm text-gray-500">
                La solution complète pour progresser rapidement.
              </p>

              <ul className="mb-6 space-y-2 text-sm text-gray-600">
                <li>✓ Cours illimités</li>
                <li>✓ Créneaux flexibles</li>
                <li>✓ {withCommitment ? 'Avec' : 'Sans'} engagement</li>
              </ul>

              <Button
                className="w-full"
                disabled={!!user?.parentId}
                onClick={() => {
                  void presenter.handleSubscribe(withCommitment)
                }}
              >
                S&#39;abonner
              </Button>
            </div>

            {/* Or divider */}
            <div className="flex items-center gap-4">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-sm text-gray-400">ou</span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>

            {/* Individual lesson payment (moved below) */}
            <div className="rounded-2xl border border-gray-300 px-6 py-3 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h4 className="text-lg font-semibold">Cours à l’unité</h4>
                  <p className="mb-3 text-sm text-gray-500">Achetez plusieurs cours.</p>
                </div>
                <span className="self-start text-4xl font-bold">
                  {(UNITARY_PRICE * quantity).toFixed(2).replace('.', ',')}€
                </span>
              </div>

              <div className="flex items-center gap-x-6">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3">
                    <Button
                      variant="secondary"
                      className="border border-gray-200 px-2"
                      onClick={() => {
                        if (quantity > 1) {
                          setQuantity((q) => q - 1)
                        }
                      }}
                    >
                      <Minus />
                    </Button>
                    <span className="flex-1 text-center text-4xl font-bold text-blue-600 sm:text-6xl">
                      {quantity.toString().padStart(2, '0')}
                    </span>
                    <Button
                      className="px-2"
                      onClick={() => {
                        setQuantity((q) => q + 1)
                      }}
                    >
                      <Plus />
                    </Button>
                  </div>
                </div>

                <Button
                  className="flex-1"
                  onClick={() => {
                    void handlePayForIndividualLessons()
                  }}
                >
                  Acheter {quantity} cours
                </Button>
              </div>
            </div>
          </div>

          {presenter.subscribeOpen && (
            <PaymentModal
              isOpen={presenter.subscribeOpen}
              onOpenChange={presenter.setSubscribeOpen}
              clientSecret={presenter.clientSecret}
              price={0}
              returnUrl={returnUrl}
              onClose={() => {
                presenter.setSubscribeOpen(false)
              }}
              policyMessage={presenter.firstSubscriptionPrice ? getPolicyMessage() : ''}
              gtmEventType={withCommitment ? 'abonnement_engagement' : 'abonnement_sans_engagement'}
            />
          )}

          {isIndividualPaymentOpen && (
            <PaymentModal
              isOpen={isIndividualPaymentOpen}
              onOpenChange={setIsIndividualPaymentOpen}
              clientSecret={individualPaymentClientSecret}
              onClose={() => {
                setIsIndividualPaymentOpen(false)
              }}
              price={UNITARY_PRICE * quantity}
              title={`Acheter un cours pour ${String(UNITARY_PRICE).replace('.', ',')}€`}
              returnUrl={returnUrl}
              gtmEventType="achat_cours_unite"
            />
          )}
        </div>
      </>
    )
  }

  if (isBookingDisabled) {
    return null
  }

  return (
    <div
      onClick={(event) => {
        event.stopPropagation()
      }}
    >
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button className="w-fit" disabled={isSuspended}>
            Réserver un cours
          </Button>
        </DialogTrigger>
        <DialogContent className="md:rounded-2xl">
          <DialogTitle className="sr-only">Réservation de cours</DialogTitle>
          <div className="flex max-h-[80vh] min-h-[60vh] max-w-[50wh] flex-col">
            {step === 1 && <BookingStepSelectDate />}
            {step === 2 && selectedHour && (
              <BookingStepPayment
                slot={{
                  time: selectedHour,
                  date: createDate(selectedDate),
                  duration: trueDuration,
                }}
                onBack={handleOnBack}
              />
            )}
            {step === 3 && selectedHour && (
              <BookingStepConfirmation
                sheet={sheet}
                isTrial={isTrialCourse && trueDuration === 30}
                slot={{
                  time: selectedHour,
                  date: createDate(selectedDate),
                  duration: trueDuration,
                }}
                onBack={handleOnBack}
                onClose={handleOnClose}
                onSubmit={handleReserveLesson}
                images={reservationImages}
                setImagesId={setReservationImages}
                isPending={isMakeReservationLoading}
                hasSubscription={subscription?.status === 'ACTIVE'}
                nextTokenBalance={Math.max(0, tokenBalance - amount)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/**
 * Booking Step: Ask for confirmation before reserving
 * @constructor
 */
const BookingStepConfirmation: React.FC<{
  sheet: Pick<Sheet, 'name'>
  isTrial: boolean
  slot: Slot
  onBack: () => void
  onClose: () => void
  onSubmit: (comment: string, images: FileDto[]) => void
  images: FileDto[]
  setImagesId: (images: FileDto[]) => void
  isPending: boolean
  hasSubscription: boolean
  nextTokenBalance: number
}> = ({
  onBack,
  sheet,
  isTrial,
  slot,
  onClose,
  images,
  setImagesId,
  onSubmit,
  isPending,
  hasSubscription,
  nextTokenBalance,
}) => {
  const formatedDate = slot.date.format('dddd D MMM')
  const [hour, minute] = slot.time.split(':')
  const formatedTime = `${slot.time} - ${slot.date.set('hour', parseInt(hour)).set('minute', parseInt(minute)).add(slot.duration, 'minutes').format('HH:mm')}`
  const [chapter, sheetName] = sheet.name.split(' : ')

  const [studentComment, setStudentComment] = useState('')
  const trimmedCommentLength = studentComment.trim().length

  return (
    <>
      {/* Mini Header */}
      <MiniHeader
        onBack={onBack}
        onClose={() => {
          onClose()
        }}
      />

      {/* Header */}
      <h2 className="mb-1 mt-2 text-3xl font-semibold">
        Confirmer votre <br />
        réservation
      </h2>
      {/* Content */}
      <div className="flex-1 overflow-y-auto py-3">
        <div className="flex flex-col gap-y-2">
          <div className="my-2 flex w-full justify-between gap-x-6 rounded-2xl border border-gray-200 p-4">
            <div className="flex max-w-[60%] flex-col justify-between">
              <div className="text-xs uppercase text-gray-500">
                {sheetName ? chapter : 'Chapitre'}
              </div>
              <div className="font-medium">{sheetName ?? chapter}</div>
            </div>
            <div className="flex flex-col font-semibold">
              <div className="capitalize">{formatedDate}</div>
              <div className="">{formatedTime}</div>
            </div>
          </div>

          <div className="mb-4 mt-0 flex flex-col gap-y-1">
            <label htmlFor="studentComment" className="text-sm font-medium text-gray-700">
              Un commentaire pour votre professeur ? ({isTrial ? 'obligatoire' : 'optionnel'})
            </label>
            <Textarea
              id="studentComment"
              placeholder="Ex: Quelle(s) partie(s) du cours vous avez des difficultés ?"
              className="w-full"
              value={studentComment}
              maxLength={126}
              required={isTrial}
              minLength={isTrial ? MIN_TRIAL_COMMENT_LENGTH : undefined}
              aria-describedby={isTrial ? 'studentComment-requirement' : undefined}
              onChange={(e) => {
                setStudentComment(e.target.value)
              }}
            />
            {isTrial && trimmedCommentLength < MIN_TRIAL_COMMENT_LENGTH && (
              <p id="studentComment-requirement" className="text-sm text-red-600">
                Le commentaire doit contenir au moins {MIN_TRIAL_COMMENT_LENGTH} caractères.
              </p>
            )}
          </div>

          <div className="mb-4 mt-0 flex flex-col gap-y-1">
            <label className="text-sm font-medium text-gray-700">
              Des images à partager ? (optionnel)
            </label>
            <FileInput
              accept="image/*,application/pdf"
              files={images}
              setFiles={setImagesId}
              multiple
            />
          </div>

          {hasSubscription ? (
            <div className="text-gray-700">
              Vous pourrez annuler <strong>jusqu’à 12h avant le cours</strong>. Passé ce délai ou en
              cas d’absence, vous devrez attendre 48h avant de pouvoir réserver à nouveau.
            </div>
          ) : (
            <div className="text-gray-700">
              Vous pourrez annuler <strong>jusqu’à 12h avant le cours</strong>, passé ce délai le
              cours sera compté comme utilisé même en cas d’absence.
            </div>
          )}

          {!hasSubscription && (
            <p className="text-sm text-gray-600">
              Il vous restera {nextTokenBalance} cours après cette réservation.
            </p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="h-fit pt-4">
        <Button
          className="w-full"
          disabled={isPending || (isTrial && trimmedCommentLength < MIN_TRIAL_COMMENT_LENGTH)}
          onClick={() => {
            onSubmit(studentComment, images)
          }}
        >
          {isPending ? <LoaderCircle className="h-4 w-4 animate-spin" /> : 'Réserver le cours'}
        </Button>
      </div>
    </>
  )
}

function PaymentModal({
  isOpen,
  onOpenChange,
  clientSecret,
  onClose,
  title,
  returnUrl,
  policyMessage,
  gtmEventType,
}: {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  clientSecret: string | null
  onClose: () => void
  price: number | null
  returnUrl: string
  policyMessage?: string
  title?: string
  gtmEventType?: 'achat_cours_unite' | 'abonnement_engagement' | 'abonnement_sans_engagement'
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col gap-8">
        <DialogHeader className="flex flex-row items-end justify-between">
          <DialogTitle className="justify-between text-xl font-bold text-gray-950">
            <div>{title ?? "M'abonner à l'offre Viastud"}</div>
          </DialogTitle>
          <DialogClose asChild>
            <Button variant="none" className="m-0 h-6 w-6 justify-start p-0" onClick={onClose}>
              <X className="h-6 w-6" />
            </Button>
          </DialogClose>
        </DialogHeader>
        {!!clientSecret && (
          <Elements
            options={{
              clientSecret: clientSecret,
              appearance: {
                theme: 'flat',
                variables: {
                  colorPrimary: '#3347FF',
                  colorBackground: '#EBF4FF',
                  colorText: '#334155',
                  colorDanger: '#df1b41',
                  borderRadius: '16px',
                },
              },
              loader: 'auto',
            }}
            stripe={getStripe()}
          >
            <PaymentForm
              returnUrl={returnUrl}
              policyMessage={policyMessage}
              gtmEventType={gtmEventType}
            />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  )
}
